import { useState, useEffect } from 'react'
import { Target, Loader2, HelpCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoShareOfVoice } from '../utils/demoData'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'

// Tooltip component
const Tooltip = ({ text, children }: { text: string; children: React.ReactNode }) => {
  const [show, setShow] = useState(false)

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444']

// Provider badge configurations
const PROVIDERS = {
  OPENAI: { name: 'ChatGPT', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: '🤖' },
  ANTHROPIC: { name: 'Claude', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '🧠' },
  GOOGLE: { name: 'Google', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '✨' }
}

export default function ShareOfVoiceCard() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [loading, setLoading] = useState(true)
  const [sovData, setSovData] = useState<any[]>([])
  const [yourBrandSoV, setYourBrandSoV] = useState<number>(0)
  const [showTrendModal, setShowTrendModal] = useState(false)

  // Trend data (6 months) - only use demo values in demo mode
  const trendData = isDemoMode ? [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 18.5 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 20.2 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 21.8 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 23.1 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 24.7 },
    { date: new Date(Date.now()).toISOString(), value: yourBrandSoV || 26.3 }
  ] : [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now()).toISOString(), value: yourBrandSoV }
  ]

  useEffect(() => {
    fetchShareOfVoice()
  }, [activeBrand?.id, isDemoMode])

  const fetchShareOfVoice = async () => {
    if (!activeBrand?.id && !isDemoMode) return

    try {
      setLoading(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        const data = demoShareOfVoice.data
        setSovData(data)

        const yourBrand = data.find((b: any) => b.isYourBrand)
        if (yourBrand) {
          setYourBrandSoV(yourBrand.shareOfVoice)
        }
        setLoading(false)
        return
      }

      const response = await api.get(`/competitors/${activeBrand!.id}/share-of-voice`)

      if (response.data.success && response.data.data) {
        const data = response.data.data
        setSovData(data)

        // Find your brand's SoV
        const yourBrand = data.find((b: any) => b.isYourBrand)
        if (yourBrand) {
          setYourBrandSoV(yourBrand.shareOfVoice)
        }
      }
    } catch (error) {
      console.error('Failed to fetch Share of Voice:', error)
    } finally {
      setLoading(false)
    }
  }

  // Badge shows month-over-month change (current vs last month)
  const lastMonthSoV = trendData[trendData.length - 2]?.value || yourBrandSoV
  const monthOverMonthChange = yourBrandSoV - lastMonthSoV
  const monthOverMonthPercent = lastMonthSoV > 0
    ? Math.abs((monthOverMonthChange / lastMonthSoV) * 100)
    : 0

  const trend = yourBrandSoV > lastMonthSoV ? 'up' : yourBrandSoV < lastMonthSoV ? 'down' : 'stable'

  // Prepare pie chart data
  const pieData = sovData.map((brand, index) => ({
    name: brand.name,
    value: brand.shareOfVoice,
    color: brand.isYourBrand ? COLORS[0] : COLORS[(index % COLORS.length)]
  }))

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p className="text-sm font-semibold">{payload[0].name}</p>
          <p className="text-xs text-indigo-600">{payload[0].value.toFixed(1)}%</p>
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-medium text-gray-600">Share of Voice</h3>
          </div>
        </div>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
        </div>
      </div>
    )
  }

  // Check if there's any actual data (not just empty records)
  const hasData = sovData.length > 0 && sovData.some((brand: any) => brand.totalMentions > 0)

  if (!hasData) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-600">Share of Voice</h3>
          </div>
          <TrendBadge
            hasHistoricalData={true}
            trend="stable"
            value={0}
            unit="%"
            decimals={1}
            onClick={() => setShowTrendModal(true)}
          />
        </div>
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No mentions found</p>
          <p className="text-xs text-gray-400 mt-2">
            Your brand and competitors were not mentioned in any of the searched queries.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-medium text-gray-600">Share of Voice</h3>
            <Tooltip text="Percentage of AI mentions your brand receives compared to competitors. Higher share of voice indicates stronger market presence in AI responses.">
              <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </Tooltip>
          </div>
          <TrendBadge
            hasHistoricalData={true}
            trend={trend}
            value={monthOverMonthPercent}
            unit="%"
            decimals={1}
            onClick={() => setShowTrendModal(true)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-indigo-600">
                {yourBrandSoV.toFixed(1)}%
              </p>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              of AI mentions
            </p>
            {(() => {
              const yourBrand = sovData.find((b: any) => b.isYourBrand)
              if (yourBrand && yourBrand.mentions > 0) {
                // Count only providers that mentioned YOUR brand
                const yourProviders = yourBrand.providers || []
                return (
                  <p className="text-xs text-gray-400 mt-2">
                    {yourBrand.mentions} mention{yourBrand.mentions !== 1 ? 's' : ''} across {yourProviders.length} AI provider{yourProviders.length !== 1 ? 's' : ''}
                  </p>
                )
              }
              return null
            })()}
          </div>

          {/* Mini Pie Chart */}
          <div className="w-24 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={35}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Breakdown */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="space-y-3">
            {sovData.slice(0, 3).map((brand, index) => (
              <div key={brand.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: pieData[index]?.color }}
                    />
                    <span className={`${brand.isYourBrand ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                      {brand.name}
                      {brand.isYourBrand && <span className="ml-1 text-indigo-600">(You)</span>}
                    </span>
                  </div>
                  <span className="font-medium text-gray-700">{brand.shareOfVoice.toFixed(1)}%</span>
                </div>

                {/* Provider badges */}
                {brand.providers && brand.providers.length > 0 && (
                  <div className="flex items-center gap-1 ml-4">
                    {brand.providers.map((provider: string) => (
                      <span
                        key={provider}
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${
                          PROVIDERS[provider as keyof typeof PROVIDERS]?.color || 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                        title={PROVIDERS[provider as keyof typeof PROVIDERS]?.name || provider}
                      >
                        {PROVIDERS[provider as keyof typeof PROVIDERS]?.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="Share of Voice Trend"
        metricName="Share of Voice"
        data={trendData}
        color="#4F46E5"
        unit="%"
      />
    </div>
  )
}
